import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsCategoryOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || !isHome
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            className="flex items-center gap-2 group"
          >
            <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-play-circle-fill text-xl"></i>
            </div>
            <span className={`font-heading text-xl font-bold transition-colors ${
              isScrolled || !isHome ? 'text-foreground-900' : 'text-background-50'
            }`}>
              精品视频
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                location.pathname === '/'
                  ? 'bg-primary-500 text-background-50'
                  : isScrolled || !isHome
                    ? 'text-foreground-700 hover:text-foreground-900 hover:bg-background-100'
                    : 'text-background-50/90 hover:text-background-50 hover:bg-white/10'
              }`}
            >
              首页
            </Link>

            <div className="relative">
              <button
                onClick={() => setIsCategoryOpen(!isCategoryOpen)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-1 ${
                  isCategoryOpen
                    ? 'bg-primary-500 text-background-50'
                    : isScrolled || !isHome
                      ? 'text-foreground-700 hover:text-foreground-900 hover:bg-background-100'
                      : 'text-background-50/90 hover:text-background-50 hover:bg-white/10'
                }`}
              >
                分类浏览
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isCategoryOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isCategoryOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setIsCategoryOpen(false)}></div>
                  <div className="absolute top-full left-0 mt-2 w-56 bg-background-50 rounded-xl border border-background-200/80 shadow-lg py-2 z-50">
                    {categories.map((cat) => (
                      <Link
                        key={cat.id}
                        to={`/category/${cat.slug}`}
                        className="flex items-center gap-3 px-4 py-2.5 hover:bg-background-100 transition-colors text-sm text-foreground-700 hover:text-foreground-900"
                      >
                        <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${cat.color}`}>
                          <i className={`${cat.icon} text-sm`}></i>
                        </div>
                        <span>{cat.name}</span>
                      </Link>
                    ))}
                  </div>
                </>
              )}
            </div>

            <Link
              to="/about"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                location.pathname === '/about'
                  ? 'bg-primary-500 text-background-50'
                  : isScrolled || !isHome
                    ? 'text-foreground-700 hover:text-foreground-900 hover:bg-background-100'
                    : 'text-background-50/90 hover:text-background-50 hover:bg-white/10'
              }`}
            >
              关于我们
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <button className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors ${
              isScrolled || !isHome
                ? 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                : 'text-background-50/80 hover:text-background-50 hover:bg-white/10'
            }`}>
              <i className="ri-search-line text-lg"></i>
            </button>
            <button className="px-5 py-2 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">
              登录
            </button>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden w-9 h-9 flex items-center justify-center rounded-lg transition-colors ${
              isScrolled || !isHome
                ? 'text-foreground-700'
                : 'text-background-50'
            }`}
          >
            <i className={`text-xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 animate-in slide-in-from-top">
          <div className="px-4 py-3 space-y-1">
            <Link to="/" className="block px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors">
              首页
            </Link>
            <div className="text-sm font-medium text-foreground-500 px-3 py-2">分类浏览</div>
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-background-100 transition-colors text-sm text-foreground-700"
              >
                <div className={`w-7 h-7 flex items-center justify-center rounded-md ${cat.color}`}>
                  <i className={`${cat.icon} text-xs`}></i>
                </div>
                <span>{cat.name}</span>
              </Link>
            ))}
            <Link to="/about" className="block px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors">
              关于我们
            </Link>
            <button className="w-full mt-2 px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">
              登录
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}